import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { REGISTRATION_SOURCE } from '@/common/functions/getUtms';
import { useGuestAuth } from '@/common/contexts/GuestAuthContext';
import { useAccountPlanGate } from '@/common/hooks/useAccountPlanGate';
import {
	createFinalCustomerCharacter,
	generateStudioPortrait,
} from '@/api/query/customer';
import { useLanguageData, useLanguage } from '@/common/hooks/useLanguageData';
import storageManager from '@/api/storageManager';
import GenderStage from './components/GenderStage';
import OptionGridStage from './components/OptionGridStage';
import AgeStage from './components/AgeStage';
import FinalDetailsStage from './components/FinalDetailsStage';
import GeneratingStage from './components/GeneratingStage';
import ResultStage from './components/ResultStage';
import StudioProgressBar from './components/StudioProgressBar';
import { StudioVoicePreviewProvider } from './context/StudioVoicePreviewContext';
import { useStudioWizard } from './hooks/useStudioWizard';
import { useStudioQuizOptions } from './hooks/useStudioQuizOptions';
import { useStudioAssetPreload } from './hooks/useStudioAssetPreload';
import { STUDIO_RESULT_IMAGES } from './constants/studioFinalOptions';
import { VIDEO_GENERATION_SECONDS } from '@/common/constants/generationEstimates';
import {
	StudioHeader,
	StudioHeaderBack,
	StudioHeaderCopy,
	StudioProgressRail,
	StudioScreen,
	StudioStageContent,
	StudioStepButton,
	StudioView,
} from './studio.styles';

const STEP_TITLE_KEYS = {
	gender: 'chooseGender',
	ethnicity: 'chooseEthnicity',
	age: 'chooseAge',
	hairstyle: 'chooseHairstyle',
	haircolor: 'chooseHairColor',
	bodytype: 'chooseBodyType',
	eyecolor: 'chooseEyeColor',
	intimatesize: 'chooseBreastSize',
	final: 'finalTitle',
	generating: 'creatingSoftie',
	result: 'resultTitle',
};

const STEP_ARIA_KEYS = {
	ethnicity: 'chooseEthnicity',
	age: 'chooseAge',
	hairstyle: 'chooseHairstyle',
	haircolor: 'chooseHairColor',
	bodytype: 'chooseBodyType',
	eyecolor: 'chooseEyeColor',
	intimatesize: 'chooseBreastSize',
};

const Studio = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { isGuest, openAuthModal, openPlanPaywall, isLoggedIn } =
		useGuestAuth();
	const { needsPlan, isAccountActive } = useAccountPlanGate(!isGuest);
	const t = useLanguageData('studio');
	const { lang } = useLanguage();
	const { data: quizOptions } = useStudioQuizOptions(lang);
	const voicePreviewFileIds = useMemo(
		() =>
			quizOptions?.voice
				?.map((option) => option.previewAudioFileId)
				.filter(Boolean) ?? [],
		[quizOptions?.voice],
	);
	const studioReturnPath = `${location.pathname}${location.search}`;
	const autoGenerateTriggeredRef = useRef(false);
	const authPromptedRef = useRef(false);
	const searchParams = new URLSearchParams(location.search);
	const shouldAutoGenerate =
		searchParams.get('autoGenerate') === '1' ||
		storageManager.studio.isQuizAutoGeneratePending();
	const {
		step,
		gender,
		age,
		setAge,
		selections,
		finalDetails,
		portraitFile,
		setPortraitFile,
		setPortraitPrompts,
		updateFinalDetail,
		randomizeName,
		canGoBack,
		optionsForStep,
		selectGender,
		selectOption,
		confirmAge,
		confirmFinal,
		goBack,
		goToStep,
		buildCharacterPayload,
		resetStudioStorage,
		totalProgressSteps,
		currentProgressStep,
	} = useStudioWizard(lang, quizOptions);

	useStudioAssetPreload({ step, gender });

	const portraitMutation = useMutation({
		mutationFn: (payload) => generateStudioPortrait(payload),
		onSuccess: ({ data }) => {
			setPortraitFile(data?.image ?? null);
			setPortraitPrompts({
				positive: data?.prompt,
				negative: data?.negativePrompt,
			});
		},
		onError: () => {
			goToStep('final');
		},
	});

	const createMutation = useMutation({
		mutationFn: (payload) =>
			createFinalCustomerCharacter({
				...payload,
				generate_video: true,
			}),
		onSuccess: ({ data }) => {
			resetStudioStorage();
			if (data?._id) {
				navigate(`/chat/${data._id}`);
				return;
			}
			navigate('/my-softies');
		},
	});

	const runQuizAutoGenerate = useCallback(() => {
		if (autoGenerateTriggeredRef.current || createMutation.isPending) {
			return;
		}

		autoGenerateTriggeredRef.current = true;
		storageManager.studio.consumeQuizAutoGeneratePending();

		const payload = buildCharacterPayload();

		if (portraitFile?._id) {
			createMutation.mutate(payload);
			return;
		}

		portraitMutation.mutate(payload, {
			onSuccess: ({ data }) => {
				createMutation.mutate({
					...payload,
					portrait_image_id: data?.image?._id,
					generated_photo_prompt: data?.prompt,
					generated_negative_photo_prompt: data?.negativePrompt,
				});
			},
			onError: () => {
				autoGenerateTriggeredRef.current = false;
			},
		});
	}, [buildCharacterPayload, createMutation, portraitFile, portraitMutation]);

	const handleConfirmFinal = useCallback(() => {
		if (isGuest) {
			openAuthModal('register', studioReturnPath, REGISTRATION_SOURCE.STUDIO);
			return;
		}

		if (needsPlan || isAccountActive === false) {
			openPlanPaywall(studioReturnPath);
			return;
		}

		confirmFinal();
	}, [
		confirmFinal,
		isAccountActive,
		isGuest,
		needsPlan,
		openAuthModal,
		openPlanPaywall,
		studioReturnPath,
	]);

	const handleAwaken = useCallback(() => {
		if (isGuest) {
			openAuthModal('register', studioReturnPath, REGISTRATION_SOURCE.STUDIO);
			return;
		}

		createMutation.mutate(buildCharacterPayload());
	}, [
		buildCharacterPayload,
		createMutation,
		isGuest,
		openAuthModal,
		studioReturnPath,
	]);

	useEffect(() => {
		if (
			shouldAutoGenerate ||
			isGuest ||
			step !== 'result' ||
			portraitFile ||
			portraitMutation.isPending
		) {
			return;
		}

		portraitMutation.mutate(buildCharacterPayload());
	}, [step, portraitFile, isGuest, shouldAutoGenerate]);

	useEffect(() => {
		if (!shouldAutoGenerate || step !== 'result') {
			return;
		}

		if (isGuest) {
			if (!authPromptedRef.current) {
				authPromptedRef.current = true;
				openAuthModal(
					'register',
					studioReturnPath,
					REGISTRATION_SOURCE.STUDIO,
					{
						prefilledEmail: searchParams.get('email'),
						autoAdvanceEmail: true,
					},
				);
			}
			return;
		}

		runQuizAutoGenerate();
	}, [
		isGuest,
		isLoggedIn,
		openAuthModal,
		runQuizAutoGenerate,
		shouldAutoGenerate,
		step,
		studioReturnPath,
	]);

	const fallbackImage =
		(STUDIO_RESULT_IMAGES[gender] ?? STUDIO_RESULT_IMAGES.woman)[0] ??
		STUDIO_RESULT_IMAGES.woman[0];

	const stepTitle =
		step === 'intimatesize' && gender === 'men'
			? t.chooseBulgeSize
			: (t[STEP_TITLE_KEYS[step]] ?? t.chooseGender);

	if (createMutation.isPending) {
		return (
			<StudioView id='createView' data-view='create'>
				<StudioScreen data-gender={gender} data-studio-step='generating'>
					<StudioStageContent>
						<GeneratingStage
							labels={t}
							totalSeconds={VIDEO_GENERATION_SECONDS}
							timeRemainingKey="estimatedVideoTimeRemaining"
						/>
					</StudioStageContent>
				</StudioScreen>
			</StudioView>
		);
	}

	return (
		<StudioVoicePreviewProvider fileIds={voicePreviewFileIds}>
			<StudioView id="createView" data-view="create">
				<StudioScreen data-gender={gender} data-studio-step={step}>
					<StudioHeader $hasBack={canGoBack}>
						{canGoBack && (
							<StudioHeaderBack aria-label={t.navigation}>
								<StudioStepButton
									id="studioBack"
									type="button"
									$back
									$inHeader
									aria-label={t.back}
									onClick={goBack}
								>
									<svg viewBox="0 0 24 24" aria-hidden="true">
										<path d="m15 18-6-6 6-6" />
									</svg>
								</StudioStepButton>
							</StudioHeaderBack>
						)}
						<StudioHeaderCopy $hasBack={canGoBack}>
							<h1>
								{t.createTitlePrefix} <span>{t.createTitleHighlight}</span>
							</h1>
							<p id="studioStepTitle">{stepTitle}</p>
						</StudioHeaderCopy>
					</StudioHeader>

					<StudioProgressRail>
						<StudioProgressBar
							currentIndex={currentProgressStep}
							maxSteps={totalProgressSteps}
						/>
					</StudioProgressRail>

					<StudioStageContent>
					{step === 'gender' && (
						<GenderStage
							labels={t.gender}
							selectedGender={gender}
							onSelect={selectGender}
						/>
					)}

					{step === 'age' && (
						<AgeStage
							age={age}
							onAgeChange={setAge}
							onConfirm={confirmAge}
							labels={t}
						/>
					)}

					{[
						'ethnicity',
						'hairstyle',
						'haircolor',
						'bodytype',
						'eyecolor',
						'intimatesize',
					].includes(step) && (
						<OptionGridStage
							lang={lang}
							stepKey={step}
							gender={gender}
							options={optionsForStep(step)}
							selectedLabel={selections[step]}
							onSelect={selectOption}
							gridOptionLabels={t.gridOptionLabels}
							ariaLabel={
								step === 'intimatesize' && gender === 'men'
									? t.chooseBulgeSize
									: t[STEP_ARIA_KEYS[step]]
							}
						/>
					)}

					{step === 'final' && (
						<FinalDetailsStage
							finalDetails={finalDetails}
							onUpdate={updateFinalDetail}
							onRandomizeName={randomizeName}
							onConfirm={handleConfirmFinal}
							labels={t}
							lang={lang}
							quizOptions={quizOptions}
						/>
					)}

					{step === 'generating' && (
						<GeneratingStage
							labels={t}
							onComplete={() => goToStep('result')}
						/>
					)}

					{step === 'result' && (
						<ResultStage
							gender={gender}
							age={age}
							finalDetails={finalDetails}
							selections={selections}
							portraitFile={portraitFile}
							fallbackImage={fallbackImage}
							onReroll={() => portraitMutation.mutate(buildCharacterPayload())}
							onAwaken={handleAwaken}
							isGenerating={portraitMutation.isPending}
							isAwakening={createMutation.isPending}
							labels={t}
							lang={lang}
							quizOptions={quizOptions}
						/>
					)}
				</StudioStageContent>
			</StudioScreen>
		</StudioView>
		</StudioVoicePreviewProvider>
	);
};

export default Studio;
